/**
 * Decisões da inbox ao vivo.
 * A tela atualiza pelo aviso do banco. Estas funções dizem quando uma busca
 * ainda é necessária e quando ela só repetiria o que o aviso já entregou.
 */

export const INBOX_STALE_CHECK_MS = 60_000;
export const INBOX_REALTIME_HEALTHY_GRACE_MS = 60_000;
/** Aba escondida por menos que isso mantém o canal ligado e não rebusca. */
export const INBOX_HIDDEN_GRACE_MS = 30_000;
/** Espera o insert de messages chegar antes de uma busca única de segurança. */
export const INBOX_OPEN_CHAT_SYNC_DELAY_MS = 400;

export function shouldFetchMessagesAfterConversationUpdate(params: {
  conversationLastMessageAt: string;
  knownLastMessageAt: string | null;
}): boolean {
  if (!params.knownLastMessageAt) return true;
  return params.conversationLastMessageAt > params.knownLastMessageAt;
}

export function shouldRunInboxSafetyCheck(params: {
  documentHidden: boolean;
  isLeaderTab: boolean;
  hubRecentlyActive: boolean;
}): boolean {
  if (params.documentHidden || !params.isLeaderTab) return false;
  return !params.hubRecentlyActive;
}

export type InboxVersionedRow = {
  conversation_id: string;
  updated_at?: string;
  version?: number;
};

/**
 * Linhas do delta que a lista ainda não refletiu.
 * Mesma versão já carregada não volta para o banco (contato, etiqueta, conexão).
 */
export function inboxDeltaRowsToRefresh<T extends InboxVersionedRow>(
  loaded: readonly T[],
  incoming: readonly T[],
): T[] {
  const byId = new Map(loaded.map((row) => [row.conversation_id, row]));
  return incoming.filter((row) => {
    const prev = byId.get(row.conversation_id);
    if (!prev) return true;
    if (typeof row.version === "number" && typeof prev.version === "number") {
      return row.version > prev.version;
    }
    if (row.updated_at && prev.updated_at) {
      return row.updated_at > prev.updated_at;
    }
    return true;
  });
}

export function pickInboxSafetyLeader(tabIds: readonly string[]): string | null {
  if (tabIds.length === 0) return null;
  return [...tabIds].sort()[0] ?? null;
}

/** Uma busca em andamento absorve os pedidos seguintes e roda no máximo mais uma vez. */
export function createSingleFlight(run: () => Promise<void>): () => Promise<void> {
  let current: Promise<void> | null = null;
  let queued = false;

  return () => {
    if (current) {
      queued = true;
      return current;
    }

    const execute = async () => {
      do {
        queued = false;
        await run();
      } while (queued);
    };

    current = execute().finally(() => {
      current = null;
    });
    return current;
  };
}

/**
 * Mensagens recebidas usam o horário do WhatsApp, não o horário em que entraram no banco.
 * Uma mensagem pode entrar depois de outra mais nova. A busca de lacuna volta esse tanto
 * para não pular a mensagem atrasada. Linhas repetidas são descartadas pelo id.
 */
export const CHAT_GAP_OVERLAP_MS = 5 * 60_000;

/** Volta da busca de lacuna da lista. Cobre commits atrasados em relação ao updated_at. */
export const INBOX_DELTA_OVERLAP_MS = 60_000;

/** Maior horário do servidor entre as linhas. Ignora vazios e inválidos. */
export function maxServerTimestamp(
  current: string | null,
  values: ReadonlyArray<string | null | undefined>,
): string | null {
  let best = current;
  let bestMs = current ? Date.parse(current) : Number.NEGATIVE_INFINITY;
  if (Number.isNaN(bestMs)) bestMs = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (!value) continue;
    const ms = Date.parse(value);
    if (Number.isNaN(ms) || ms <= bestMs) continue;
    best = value;
    bestMs = ms;
  }
  return best;
}

export function sinceWithOverlap(watermark: string | null, overlapMs: number): string | null {
  if (!watermark) return null;
  const ms = Date.parse(watermark);
  if (Number.isNaN(ms)) return null;
  return new Date(ms - overlapMs).toISOString();
}

/** Algum item carregado tem exatamente esse horário (formatos diferentes do mesmo instante contam). */
export function hasMessageAtInstant(
  createdAts: Iterable<string | null | undefined>,
  instant: string,
): boolean {
  const target = Date.parse(instant);
  if (Number.isNaN(target)) return false;
  for (const value of createdAts) {
    if (value && Date.parse(value) === target) return true;
  }
  return false;
}
